import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, X } from 'lucide-react';
import BlogLayout from './BlogLayout';
import BlogCard from './BlogCard';
import usePublicPosts from '../../hooks/usePublicPosts';
import { OptimizedSpotlightGroup } from '../OptimizedLandingPage';
import { BlogListSkeleton } from '../loading/SkeletonLoader';
import { useLoading } from '../../contexts/LoadingContext';
import {
  searchPosts,
  filterPostsByTag,
  getAllTags,
  sortPostsByDate
} from '../../utils/blogUtils';

const BlogList = ({ isMenuOpen, setIsMenuOpen }) => {
  const { posts, loading, error, getPublishedPosts } = usePublicPosts();
  const { setLoading } = useLoading();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  // Sync loading state with global loading context
  useEffect(() => {
    setLoading('blogList', loading);
    return () => setLoading('blogList', false);
  }, [loading, setLoading]);

  // Get published posts and available tags
  const publishedPosts = useMemo(() => getPublishedPosts(), [posts, getPublishedPosts]);
  const availableTags = useMemo(() => getAllTags(publishedPosts), [publishedPosts]);

  // Filter and search posts
  const filteredPosts = useMemo(() => {
    let filtered = publishedPosts;
    
    // Apply tag filter
    if (selectedTag) {
      filtered = filterPostsByTag(filtered, selectedTag);
    }
    
    // Apply search
    if (searchQuery.trim()) {
      filtered = searchPosts(filtered, searchQuery);
    }
    
    // Sort by date (newest first)
    return sortPostsByDate(filtered);
  }, [publishedPosts, selectedTag, searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleTagChange = (e) => {
    setSelectedTag(e.target.value);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTag('');
  };

  if (loading) {
    return (
      <BlogLayout
        title="Blog"
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      >
        <BlogListSkeleton />
      </BlogLayout>
    );
  }

  if (error) {
    return (
      <BlogLayout 
        title="Blog" 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
      >
        <div className="blog-content">
          <div className="empty-state">
            <h2 className="empty-state-title">Error Loading Blog</h2>
            <p>{error}</p>
          </div>
        </div>
      </BlogLayout>
    );
  }

  return (
    <BlogLayout 
      title="Blog" 
      description="Explore our latest insights about technology, development, and innovation"
      keywords="blog, technology, development, programming, web development, tutorials"
      isMenuOpen={isMenuOpen} 
      setIsMenuOpen={setIsMenuOpen}
    >
      <div className="blog-content">
        {/* Blog Header */}
        <header className="blog-header">
          <h1 className="blog-title">oooooOOOOOOOO Javorski</h1>
          <p className="blog-subtitle">
            Security researcher with focus on game anti-cheat technologies and operating system internals.
          </p>
        </header>

        {/* Search and Filter Controls */}
        <div className="blog-controls">
          <div className="blog-controls-row">
            <div className="search-input form-group">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="form-input pl-10"
                />
              </div>
            </div>

            <div className="filter-select form-group">
              <div className="relative">
                <select
                  value={selectedTag}
                  onChange={handleTagChange}
                  className="form-select pl-10"
                >
                  <option value="">Filter by tags</option>
                  {availableTags.map(tag => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {(searchQuery || selectedTag) && (
            <button
              onClick={clearFilters}
              className="admin-btn secondary clear-filters-btn"
              title="Clear Filters"
            >
              <X size={16} />
            </button>
          )}

        </div>

        {/* Results Info */}
        {(searchQuery || selectedTag) && (
          <div className="mb-6">
            <p className="text-slate-400">
              {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''} found
              {searchQuery && ` for "${searchQuery}"`}
              {selectedTag && ` in "${selectedTag}"`}
            </p>
          </div>
        )}

        {/* Blog Posts Grid */}
        {filteredPosts.length > 0 ? (
          <OptimizedSpotlightGroup>
            <div className="blog-grid">
              {filteredPosts.map(post => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          </OptimizedSpotlightGroup>
        ) : (
          <div className="empty-state">
            <h2 className="empty-state-title">
              {searchQuery || selectedTag ? 'No Posts Found' : 'No Blog Posts Yet'}
            </h2>
            <p>
              {searchQuery || selectedTag 
                ? 'Try adjusting your search or filter criteria.'
                : 'Check back soon for new content!'
              }
            </p>
          </div>
        )}
      </div>
    </BlogLayout>
  );
};

export default BlogList;